/**
 * Extract the real error message from a Supabase Edge Function failure.
 *
 * `supabase.functions.invoke()` returns a generic `FunctionsHttpError`
 * ("Edge Function returned a non-2xx status code") and hides the response
 * body. This helper reads the actual JSON/text body the function returned
 * so we can show admins exactly what failed (function name + status + body).
 */
export async function extractEdgeFunctionError(
  functionName: string,
  error: unknown,
): Promise<string> {
  if (!error) return `[${functionName}] Unknown error`;

  const anyErr = error as any;
  const ctx = anyErr?.context;

  // FunctionsHttpError / FunctionsRelayError have a `context` Response
  if (ctx && typeof ctx === "object" && typeof ctx.text === "function") {
    const status: number | undefined = ctx.status;
    let body = "";
    try {
      body = await ctx.text();
    } catch {
      /* ignore */
    }

    // Try to parse as JSON for nicer formatting
    let parsedDetail = body;
    try {
      const j = JSON.parse(body);
      parsedDetail =
        j.error?.message ||
        j.message ||
        j.error ||
        j.details ||
        JSON.stringify(j);
    } catch {
      /* keep raw text */
    }

    const trimmed = (parsedDetail || "").toString().slice(0, 800);
    return `[${functionName}${status ? ` ${status}` : ""}] ${trimmed || anyErr.message || "no body"}`;
  }

  if (anyErr?.message) return `[${functionName}] ${anyErr.message}`;
  return `[${functionName}] ${String(error)}`;
}

/** Format a Supabase Storage error with the bucket/path context. */
export function formatStorageError(
  bucket: string,
  path: string,
  error: unknown,
): string {
  const anyErr = error as any;
  const msg =
    anyErr?.message ||
    anyErr?.error ||
    (typeof error === "string" ? error : JSON.stringify(error));
  return `[storage:${bucket}/${path}] ${msg}`;
}
